'use client'

import Script from 'next/script'
import { useEffect, useRef } from 'react'
import { trackEvent } from '@/utilities/analytics/trackEvent'

interface PatientPortalScriptsProps {
  /**
   * Per-tenant Zendesk chat visibility (Payload `Tenants.enableZendeskChat`).
   * `false` hides the Zendesk widget; `true` or `undefined` (unset) shows it.
   * Localize/translation scripts are unaffected by this prop.
   */
  enableZendeskChat?: boolean | null
}

/** Same-origin rewrite of Localize CDN; keep in sync with `next.config.js`. */
export const LOCALIZE_SCRIPT_PATH = '/localize.js'

/**
 * PatientPortalScripts Provider
 *
 * Contains third-party scripts specific to the patient portal:
 * - Zendesk chat widget
 * - Localize.js for translations
 *
 * This component should only be included in patient portal layouts,
 * not in the root layout or client portal layouts.
 */
export const PatientPortalScripts: React.FC<PatientPortalScriptsProps> = ({
  enableZendeskChat,
}) => {
  const isZendeskChatEnabled = enableZendeskChat !== false
  const lastLangRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!isZendeskChatEnabled) return
    if (typeof window === 'undefined') return

    const hookZendesk = () => {
      try {
        const zendesk = (window as unknown as { zE?: (...args: unknown[]) => void }).zE
        // Ensure zendesk messenger API exists
        if (typeof zendesk !== 'function') return false

        // Guard against multiple bindings
        if ((window as unknown as { __zendeskHooked?: boolean }).__zendeskHooked) return true
        ;(window as unknown as { __zendeskHooked?: boolean }).__zendeskHooked = true

        zendesk(() => {
          const messengerOn = (event: string, handler: (payload?: unknown) => void) => {
            try {
              zendesk('messenger:on', event, handler)
            } catch (_err) {
              // Some events are not supported depending on the widget version; ignore errors.
            }
          }

          messengerOn('open', () => {
            trackEvent('zendesk:widget_open')
          })

          messengerOn('close', () => {
            trackEvent('zendesk:widget_close')
          })
          // This event is fired when:
          // Agent/bot sends a reply while the widget is closed or the conversation view is not visible.
          // or when the user opens the widget and the message become read.
          messengerOn('unreadMessages', (count) => {
            const unread =
              typeof count === 'number'
                ? count
                : typeof count === 'object' && count !== null && 'count' in count
                  ? (count as { count?: unknown }).count
                  : undefined
            trackEvent('zendesk:unread_messages', {
              count: typeof unread === 'number' ? unread : undefined,
            })
          })
        })

        return true
      } catch (err) {
        console.warn('Zendesk hook setup failed', err)
        return false
      }
    }

    if (!hookZendesk()) {
      const maxAttempts = 50
      let attempts = 0
      const interval = setInterval(() => {
        attempts += 1
        if (hookZendesk() || attempts >= maxAttempts) {
          clearInterval(interval)
        }
      }, 100)
      return () => clearInterval(interval)
    }

    return undefined
  }, [isZendeskChatEnabled])

  useEffect(() => {
    const registerLanguageTracking = () => {
      const localize =
        typeof window !== 'undefined'
          ? ((
              window as unknown as {
                Localize?: {
                  getLanguage?: () => string
                  on?: (event: string, cb: (lang?: string) => void) => void
                }
              }
            ).Localize ?? undefined)
          : undefined
      const getLang = () => (localize?.getLanguage ? localize.getLanguage() : undefined)

      const trackLanguage = (lang?: string) => {
        const current = lang || getLang()
        if (!current || current === lastLangRef.current) return
        lastLangRef.current = current
        trackEvent('localize:language_set', { language: current })
      }

      if (!localize?.on) return false

      localize.on('setLanguage', trackLanguage)
      return true
    }

    if (registerLanguageTracking()) return

    const maxAttempts = 50
    let attempts = 0
    const interval = setInterval(() => {
      attempts += 1
      if (registerLanguageTracking() || attempts >= maxAttempts) {
        clearInterval(interval)
      }
    }, 100)
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      {isZendeskChatEnabled && (
        <>
          <Script
            id="ze-snippet"
            src="https://static.zdassets.com/ekr/snippet.js?key=89b2eb31-f746-4906-b491-1e337a5a8bf1"
            strategy="afterInteractive"
            crossOrigin="anonymous"
          />
          <Script
            id="ze-locale-sync"
            strategy="lazyOnload"
            dangerouslySetInnerHTML={{
              __html: `
            (function() {
              function syncZendeskLocale() {
                if (typeof window.zE === 'function' && window.Localize?.getLanguage) {
                  var lang = window.Localize.getLanguage();
                  if (lang && lang.length > 0) {
                    window.zE('messenger:set', 'locale', lang);
                  }
                }
              }

              function registerLocalizeListener() {
                if (window.Localize?.on && window.Localize?.getLanguage) {
                  var lang = window.Localize.getLanguage();
                  if (lang) {
                    syncZendeskLocale();
                    window.Localize.on('setLanguage', syncZendeskLocale);
                    return true;
                  }
                }
                return false;
              }

              if (!registerLocalizeListener()) {
                var attempts = 0;
                var maxAttempts = 50; // 5 seconds max
                var interval = setInterval(function() {
                  attempts++;
                  if (registerLocalizeListener() || attempts >= maxAttempts) {
                    clearInterval(interval);
                  }
                }, 100);
              }
            })();
          `,
            }}
          />
        </>
      )}
      {/* Same-origin rewrite (see next.config.js). Avoids Localize CDN CORS, which
          blocks crossOrigin="anonymous" and otherwise yields opaque Script error. */}
      <Script id="localize-script-1" src={LOCALIZE_SCRIPT_PATH} strategy="afterInteractive" />
      <Script
        id="localize-script-2"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
(function(a){if(!a.Localize){a.Localize={};for(var e=["translate","untranslate","phrase","initialize","translatePage","setLanguage","getLanguage","getSourceLanguage","detectLanguage","getAvailableLanguages","setWidgetLanguages","hideLanguagesInWidget","untranslatePage","bootstrap","prefetch","on","off","hideWidget","showWidget"],t=0;t<e.length;t++)a.Localize[e[t]]=function(){};}})(window);
Localize.initialize({
    key: 'OTr09TosZShIu',
    rememberLanguage: true,
    disableWidget: true,
    retranslateOnNewPhrases: true,
    saveNewPhrases: true, 
    autoApprove: true
});
          `,
        }}
      />
    </>
  )
}
